import React from 'react';
import { Box, Pagination, FormControl, Select, MenuItem, Typography, Stack } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

interface EnhancedTablePaginationProps {
  totalCount: number;
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** When true (default), page navigation is hidden when all rows fit on one page. */
  hidePaginationOnSinglePage?: boolean;
}

const EnhancedTablePagination: React.FC<EnhancedTablePaginationProps> = ({
  totalCount,
  page,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  hidePaginationOnSinglePage = true,
}) => {
  const theme = useTheme();
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 0;
  const safePage =
    totalCount > 0 ? Math.min(Math.max(1, page), totalPages) : 1;
  const startItem = (safePage - 1) * pageSize + 1;
  const endItem = Math.min(safePage * pageSize, totalCount);
  const showRowsPerPage = totalCount > 0;
  const showPageControls =
    totalCount > 0 && (totalPages > 1 || !hidePaginationOnSinglePage);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent:
          totalCount === 0
            ? 'center'
            : showPageControls
              ? 'space-between'
              : 'flex-end',
        p: 2,
        bgcolor: 'transparent',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.28)}`,
        flexWrap: 'wrap',
        gap: 2,
        flexShrink: 0,
      }}
    >
      {showRowsPerPage ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 'auto' }}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <FormControl size="small">
            <Select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              variant="standard"
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      ) : null}

      <Typography variant="body2" color="text.secondary">
        {totalCount === 0
          ? 'No items'
          : `${startItem}-${endItem} of ${totalCount}`}
      </Typography>

      {showPageControls ? (
        <Pagination
          count={totalPages}
          page={safePage}
          onChange={(_, newPage) => onPageChange(newPage)}
          color="primary"
          showFirstButton
          showLastButton
        />
      ) : null}
    </Box>
  );
};

export default EnhancedTablePagination;
